// ** React Imports
import { useEffect, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useHistory, useLocation, matchPath } from "react-router-dom";

// ** Store & Actions
import { useSelector, useDispatch } from "react-redux";

import { getUserProfileMain } from "store/modules/user-profile/actions/get-user-profile-main.action";
import { getUserProfileMedia } from "store/modules/user-profile/actions/get-user-profile-media.action";
import { resetUserProfileMain } from "store/modules/user-profile/user-profile-main.slice";

// ** Third Party Components
import loadable from "@loadable/component";
import { renderRoutes } from "react-router-config";

// ** Custom Components
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import Block from "components/layout/Block";
import ScrollToTop from "components/global/ScrollToTop";
import Loader, { StyledLoaderWrapper } from "components/layout/Loader";
import EmptyBlock from "components/layout/Block/BlockEmpty";

// ** User Components
import UserHeroSection from "./components/UserHeroSection";

const UserNavSection = loadable(
	() => import("./components/UserNavSection/UserNavSection"),
);

const NotAllowedComponent = loadable(
	() => import("./components/NotAllowedComponent"),
);
// ** Custom Hooks
import useUserMetaTags from "./hooks/useUserMetaTags";

// #####################################################

const User = ({
	match: {
		params: { login },
	},
	route: { routes },
}) => {
	const dispatch = useDispatch();

	const history = useHistory();
	const location = useLocation();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const isPendingBulk = useRef(false);
	const { title, metaDescription, ogMetaTags, canonicalLink } =
		useUserMetaTags();

	const userError = useSelector((state) => state.userProfileMain.error);
	const userLoading = useSelector((state) => state.userProfileMain.loading);
	const userLoaded = useSelector((state) => state.userProfileMain.loaded);
	const isAllowed = useSelector((state) => state.userProfileMain.isAllowed);
	const userId = useSelector((state) => state.userProfileMain.id);
	const userFrozen = useSelector((state) => state.userProfileMain.frozen);
	const userLogin = useSelector((state) => state.userProfileMain.login);
	const blockMe = useSelector(
		(state) => state.userProfileMain.status.blockMe,
	);
	const blockByMe = useSelector(
		(state) => state.userProfileMain.status.blockByMe,
	);

	const match = matchPath(location.pathname, {
		path: "/users/:login",
		exact: true,
		strict: true,
	});

	// ** Bulk is when the user first enters the User component. If there was a route change after the bulk, then when the user enters the Profile (child for User) the media will be updated in this component. Without this, there are bugs in the comment IDs, etc.
	useEffect(() => {
		if (location.state?.forceReloadFromLogin) {
			return;
		}

		if (
			(isAuth && !isPendingBulk.current) ||
			(!isAuth && userLogin?.toLowerCase() !== login?.toLowerCase())
		) {
			isPendingBulk.current = true;
			dispatch(getUserProfileMain({ login, isAuth })).then(() => {
				isPendingBulk.current = false;
			});
		}
	}, [login, isAuth, userLogin]);

	useEffect(() => {
		if (location.state?.forceReloadFromLogin) {
			isPendingBulk.current = true;
			dispatch(
				getUserProfileMain({
					login,
					isAuth,
				}),
			).then(() => {
				isPendingBulk.current = false;
			});
			history.replace({ ...location, state: null });
		}
	}, [location.state?.forceReloadFromLogin]);

	useEffect(() => {
		if (
			match &&
			!isPendingBulk.current &&
			!userFrozen &&
			!blockMe &&
			!blockByMe &&
			isAuth
		) {
			dispatch(
				getUserProfileMedia({
					userId,
					page: 1,
					isAuth,
					hash: null,
					infinityScroll: false,
				}),
			);
		}
	}, [location.pathname]);

	// ** Efekt w przypadku błędu (np. niepoprawny link do profilu Usera),
	// ** ustawia domyślna konfiguracje profilu oraz przenosi do strony błędu 404.
	useEffect(() => {
		if (userError) {
			dispatch(resetUserProfileMain());
			history.replace("/error404");
		}
	}, [userError]);

	const memoizedRoutes = useMemo(() => {
		return renderRoutes(routes);
	}, [routes]);

	// #####################################################

	// ** Przekierowanie na stronę błędu w przypadku nieprawidłowych parametrów w query
	if (userError) {
		history.replace("/error404");
		return null;
	}

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title} titleTemplate="%s">
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			{/* Główna zawartość strony */}
			<ScrollToTop />

			{userLoading || !userLoaded ? (
				<CardsBlockWrapper>
					<CardWrapper>
						<Block my={3} flex column>
							<StyledLoaderWrapper>
								<Loader />
							</StyledLoaderWrapper>
						</Block>
					</CardWrapper>
				</CardsBlockWrapper>
			) : (
				<CardsBlockWrapper>
					<CardWrapper>
						<UserHeroSection constantPadding={100} />

						<UserNavSection />
					</CardWrapper>

					<Block flex column>
						{isAllowed === false ? (
							<NotAllowedComponent />
						) : (
							<>{memoizedRoutes}</>
						)}
					</Block>
					<EmptyBlock />
				</CardsBlockWrapper>
			)}
		</>
	);
};

// #####################################################

export default User;

// #####################################################
